import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * CartContext — Persistent multi-distributor cart via API.
 * Replaces PreparationContext (in-memory). Same interface, server-backed.
 * 1 user = 1 draft cart. Groups by partner_id.
 */
const CartContext = createContext(null);

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be inside CartProvider');
  return ctx;
};

// Backwards-compat alias
export const usePreparation = useCart;

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [zoneModalOpen, setZoneModalOpen] = useState(false);
  const fetchedRef = useRef(false);

  // Load cart from API on mount
  const fetchCart = useCallback(async () => {
    try {
      const res = await api.get('/carts/current');
      setCart(res.data);
      return res.data;
    } catch {
      // Not authenticated or error — no cart
      setCart(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // Only fetch once. AuthContext will handle login state.
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchCart();
    }
  }, [fetchCart]);

  // Reload cart after login (listen for token changes)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !cart) {
      fetchCart();
    }
    if (!token && cart) {
      setCart(null);
      fetchedRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStorage.getItem('token')]);

  const cartId = cart?.id;

  const addItem = useCallback(async ({ product_id, product_name, product_image, distribution_id, partner_id, partner_nom, base_price, quantity, min_qty, max_qty, multiple, mechanic_id }) => {
    if (!cartId) {
      // Try fetching cart first (might have been created after login)
      const c = await fetchCart();
      if (!c?.id) { toast.error('Panier indisponible'); return false; }
    }
    const id = cart?.id || cartId;
    try {
      const res = await api.post(`/carts/${id}/add-item`, {
        product_id, product_name: product_name || 'Produit', product_image,
        distribution_id, partner_id, partner_nom: partner_nom || 'Distributeur',
        base_price: base_price || 0,
        quantity: quantity || min_qty || 1,
        min_qty, max_qty, multiple,
        mechanic_id: mechanic_id || null,
      });
      setCart(res.data);
      toast.success(`✓ Ajouté à la simulation (${partner_nom || 'Distributeur'})`);
      return true;
    } catch (err) {
      // Auto-recover stale cart: if 404 (submitted/archived), force-fetch fresh draft and retry
      if (err.response?.status === 404) {
        try {
          const freshRes = await api.get('/carts/current');
          const freshCart = freshRes.data;
          if (freshCart?.id) {
            setCart(freshCart);
            const retry = await api.post(`/carts/${freshCart.id}/add-item`, {
              product_id, product_name: product_name || 'Produit', product_image,
              distribution_id, partner_id, partner_nom: partner_nom || 'Distributeur',
              base_price: base_price || 0,
              quantity: quantity || min_qty || 1,
              min_qty, max_qty, multiple,
              mechanic_id: mechanic_id || null,
            });
            setCart(retry.data);
            toast.success(`✓ Ajouté à la simulation (${partner_nom || 'Distributeur'})`);
            return true;
          }
        } catch (retryErr) {
          toast.error(retryErr.response?.data?.detail || 'Erreur lors de l\'ajout');
          return false;
        }
      }
      const detail = err.response?.data?.detail;
      if (detail?.includes('déjà dans')) {
        toast.info('Produit déjà dans le bon de commande');
      } else {
        toast.error(detail || 'Erreur lors de l\'ajout');
      }
      return false;
    }
  }, [cartId, cart?.id, fetchCart]);

  const removeItem = useCallback(async (partnerId, idx) => {
    if (!cartId) return;
    try {
      const res = await api.delete(`/carts/${cartId}/items/${partnerId}/${idx}`);
      setCart(res.data);
    } catch {
      toast.error('Erreur suppression');
    }
  }, [cartId]);

  const updateItemQty = useCallback(async (partnerId, idx, qty) => {
    if (!cartId) return;
    const parsed = Math.max(1, parseInt(qty) || 1);
    // Optimistic update
    setCart(prev => {
      if (!prev) return prev;
      const groups = prev.groups.map(g => {
        if (g.partner_id !== partnerId) return g;
        return { ...g, items: g.items.map((it, i) => i === idx ? { ...it, quantity: parsed } : it) };
      });
      return { ...prev, groups, last_simulation: null };
    });
    try {
      const res = await api.put(`/carts/${cartId}`, { partner_id: partnerId, item_index: idx, quantity: parsed });
      setCart(res.data);
    } catch {
      // Rollback on error
      await fetchCart();
    }
  }, [cartId, fetchCart]);

  const setShippingZone = useCallback(async (zone) => {
    if (!cartId) return;
    // Optimistic
    setCart(prev => prev ? { ...prev, shipping_zone: zone } : prev);
    try {
      const res = await api.put(`/carts/${cartId}`, { shipping_zone: zone });
      setCart(res.data);
    } catch {
      await fetchCart();
    }
  }, [cartId, fetchCart]);

  const clearAll = useCallback(async () => {
    if (!cartId) return;
    try {
      await api.delete(`/carts/${cartId}`);
      // Fetch new fresh cart
      await fetchCart();
    } catch {
      toast.error('Erreur vidage panier');
    }
  }, [cartId, fetchCart]);

  const clearPartner = useCallback(async (partnerId) => {
    if (!cartId) return;
    try {
      const res = await api.delete(`/carts/${cartId}/partner/${partnerId}`);
      setCart(res.data);
    } catch {
      toast.error('Erreur suppression distributeur');
    }
  }, [cartId]);

  const requestZone = useCallback(() => setZoneModalOpen(true), []);
  const closeZoneModal = useCallback(() => setZoneModalOpen(false), []);

  // Computed values (same interface as old PreparationContext)
  const groups = {};
  (cart?.groups || []).forEach(g => { groups[g.partner_id] = g; });

  const groupsList = cart?.groups || [];
  const totalItems = groupsList.reduce((sum, g) => sum + (g.items?.length || 0), 0);
  const partnerCount = groupsList.length;
  const shippingZone = cart?.shipping_zone || '';

  return (
    <CartContext.Provider value={{
      cart, loading, groups, groupsList, shippingZone, setShippingZone,
      addItem, removeItem, updateItemQty, clearAll, clearPartner,
      zoneModalOpen, requestZone, closeZoneModal,
      totalItems, partnerCount, fetchCart,
    }}>
      {children}
    </CartContext.Provider>
  );
};
